import { PATH } from '@/constants';
import { handleOpenChat } from '@/helpers/crisp';
import {
  useDismissGuideMutation,
  useGetGuideTasksQuery,
  useGetUserPlanQuery,
  useGetOnboardingStepQuery,
  useUpdateGuideTaskMutation,
} from '@/redux/api/api.caller';
import {
  panelOpenSelector,
  setPanelOpen,
} from '@/redux/features/onboardingGuide.slice';
import { Icon } from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon, MenuHorizontalIcon, XIcon } from '@shopify/polaris-icons';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import settingsSlice from '@/redux/features/settings.slice';
import { PolarisBreakPoint } from '@/constants/enum';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  BottomSheet,
  CircularWidget,
  DiscountTransition,
  GuidePanel,
  LauncherBar,
  PanelHeader,
  ReviewCard,
  ReviewOverlay,
  SheetBody,
  SheetHeader,
  SheetMask,
  SheetPanel,
} from './styled';
import GuideContent, { capitalize, ITask } from './GuideContent';

const TASKS: ITask[] = [
  {
    key: 'customizeTracking',
    title: 'Customize your tracking page',
    description:
      'Make the tracking page match your brand. Set logo, colors, lookup methods, page content, and product recommendations.',
    path: PATH.TRACKING_PAGE,
    ctaLabel: 'Customize',
  },
  {
    key: 'setupNotifications',
    title: 'Set up shipping notifications',
    description: 'Send customers automatic tracking updates. Get alerts when shipments are delayed or have exceptions.',
    path: PATH.NOTIFICATIONS,
    ctaLabel: 'Set up',
  },
  {
    key: 'meetAssistant',
    title: 'Meet Trakow Assistant',
    description: 'Analyze shipment trends and delivery performance, powered by AI. Track fulfillment time, exceptions, and more.',
    path: PATH.ANALYTICS,
    ctaLabel: 'View analytics',
  },
  {
    key: 'connectAi',
    title: 'Connect your AI assistant',
    description: "Ask your AI Assistant about any shipment — directly from your team's chat.",
    path: PATH.SETTINGS,
    ctaLabel: 'Connect',
    isNew: true,
    avatarStack: ['claude', 'chatgpt', 'plus'],
  },
];

const REVIEW_STARS = [1, 2, 3, 4, 5];

const OnboardingGuide = () => {
  const navigate = useConfirmationNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const panelOpen = useSelector(panelOpenSelector);
  const isMobile = useMediaQuery(`(max-width: ${PolarisBreakPoint.SM})`);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);
  const [expandedKey, setExpandedKey] = useState<string | null | undefined>(undefined);
  const [showReview, setShowReview] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [pendingTaskKey, setPendingTaskKey] = useState<ITask['key'] | null>(null);

  const { data: onboardingData } = useGetOnboardingStepQuery();
  const { data: guideData, refetch } = useGetGuideTasksQuery();
  const { data: userPlanData } = useGetUserPlanQuery({});
  const showTrialOffer = userPlanData?.data?.trialInfo?.state === 'fresh';
  const [updateGuideTask] = useUpdateGuideTaskMutation();
  const [dismissGuide] = useDismissGuideMutation();

  const guide = guideData?.data;
  const onboardingDone = onboardingData?.data?.onboardingStatus === true;

  const prevGuideRef = useRef<typeof guide>(undefined);
  useEffect(() => {
    if (!guide || expandedKey === undefined) return;
    const prevGuide = prevGuideRef.current;
    prevGuideRef.current = guide;
    if (!prevGuide || !expandedKey) return;
    const fieldKey = `guidTask${capitalize(expandedKey)}` as keyof typeof guide;
    const wasDone = !!prevGuide[fieldKey];
    const nowDone = !!guide[fieldKey];
    if (!wasDone && nowDone) {
      const nextPending = TASKS.find((t) => !guide[`guidTask${capitalize(t.key)}` as keyof typeof guide]);
      setExpandedKey(nextPending?.key ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guide]);

  // Auto-mark a task done when the user navigates to its associated page.
  useEffect(() => {
    if (!guide?.showOnboardingGuide) return;
    const matchedTask = TASKS.find((t) => t.path === pathname);
    if (!matchedTask) return;
    const fieldKey = `guidTask${capitalize(matchedTask.key)}` as keyof typeof guide;
    if (guide[fieldKey] || pendingTaskKey) return;

    setPendingTaskKey(matchedTask.key);
    updateGuideTask({ taskKey: matchedTask.key })
      .unwrap()
      .then(() => {
        refetch();
        setPendingTaskKey(null);
      })
      .catch(() => {
        setPendingTaskKey(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, guide]);

  // Toggle body class for mobile padding — keeps LauncherBar from covering page content.
  useEffect(() => {
    const active = isMobile && !!onboardingDone && !!guide?.showOnboardingGuide;
    document.body.classList.toggle('has-onboarding-launcher', active);
    return () => document.body.classList.remove('has-onboarding-launcher');
  }, [isMobile, onboardingDone, guide?.showOnboardingGuide]);

  if (pathname === PATH.WELCOME) return null;
  if (!onboardingDone || !guide) return null;

  const showGuide = guide.showOnboardingGuide;

  const firstPendingKey = TASKS.find((t) => !guide[`guidTask${capitalize(t.key)}` as keyof typeof guide])?.key ?? null;
  const activeKey = expandedKey === undefined ? firstPendingKey : expandedKey;

  const completedCount = TASKS.filter((t) => guide[`guidTask${capitalize(t.key)}` as keyof typeof guide]).length;
  const pct = Math.round((completedCount / TASKS.length) * 100);

  const handleNavigate = (task: ITask) => {
    if (task.key === 'connectAi') {
      dispatch(settingsSlice.actions.handleSelected('aiAssistants'));
    }
    navigate(task.path);
  };


  const handleDismissReview = async () => {
    if (transitioning) return;
    setTransitioning(true);
    await new Promise<void>((resolve) => setTimeout(resolve, 2500));
    setShowReview(false);
    setTransitioning(false);
    navigate(PATH.PLAN, {
      state: {
        discountCode: 'WELCOME20',
        source_page: pathname,
        entry_type: 'onboarding',
        feature: 'onboarding',
      },
    });
  };

  const handleStarClick = async (star: number) => {
    setTransitioning(true);
    if (star <= 4) {
      handleOpenChat();
    } else {
      try {
        await (shopify as any)?.reviews?.request();
      } catch {
        window.open('https://apps.shopify.com/omega-order-tracking?reveal_new_review=true', '_blank');
      }
    }
    await new Promise<void>((resolve) => setTimeout(resolve, 2500));
    setShowReview(false);
    setTransitioning(false);
    navigate(PATH.PLAN, {
      state: {
        discountCode: 'WELCOME20',
        source_page: pathname,
        entry_type: 'onboarding',
        feature: 'onboarding',
      },
    });
  };

  const handleClaimGift = () => setShowReview(true);

  return (
    <>
      {showGuide &&
        (isMobile ? (
          <>
            <LauncherBar
              data-testid="onboarding-launcher"
              $pct={pct}
              $completed={guide.guideCompleted}
              onClick={() => setSheetOpen(true)}
              aria-label="Onboarding guide"
            >
              <span className="ring">
                <span className="ring-inner">{guide.guideCompleted ? '✓' : `${pct}%`}</span>
              </span>
              <span className="launcher-text">
                <span className="title">{guide.guideCompleted ? 'Setup complete 🎉' : 'Setup guide'}</span>
                <span className="subtitle">
                  {guide.guideCompleted ? 'Claim your welcome gift' : `${completedCount} of ${TASKS.length} tasks completed`}
                </span>
              </span>
              <span className="chevron">
                <Icon source={ChevronUpIcon} />
              </span>
            </LauncherBar>

            {sheetOpen && (
              <BottomSheet data-testid="onboarding-sheet">
                <SheetMask onClick={() => setSheetOpen(false)} />
                <SheetPanel>
                  <SheetHeader>
                    <span className="title">Setup guide</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <div style={{ position: 'relative' }} ref={menuRef}>
                        <button
                          onClick={() => setMenuOpen((v) => !v)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 4,
                            lineHeight: 0,
                            borderRadius: 4,
                          }}
                          aria-label="More options"
                        >
                          <Icon source={MenuHorizontalIcon} />
                        </button>
                        {menuOpen && (
                          <div
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: '100%',
                              marginTop: 4,
                              background: '#fff',
                              border: '1px solid #e4e5e7',
                              borderRadius: 8,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                              zIndex: 10,
                              minWidth: 120,
                            }}
                          >
                            <button
                              onClick={() => {
                                dismissGuide();
                                setMenuOpen(false);
                                setSheetOpen(false);
                              }}
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '8px 14px',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontSize: 13,
                                color: 'var(--p-color-text)',
                              }}
                            >
                              Dismiss
                            </button>
                          </div>
                        )}
                      </div>
                      <button
                        className="collapse-btn"
                        onClick={() => setSheetOpen(false)}
                        aria-label="Close guide"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 0 }}
                      >
                        <Icon source={ChevronDownIcon} />
                      </button>
                    </div>
                  </SheetHeader>
                  <SheetBody>
                    <GuideContent
                      guide={guide}
                      tasks={TASKS}
                      completedCount={completedCount}
                      pct={pct}
                      activeKey={activeKey}
                      pendingTaskKey={pendingTaskKey}
                      setExpandedKey={(key: string | null) => setExpandedKey(key)}
                      handleNavigate={handleNavigate}
                      onClaimGift={handleClaimGift}
                      showTrialOffer={showTrialOffer}
                    />
                  </SheetBody>
                </SheetPanel>
              </BottomSheet>
            )}
          </>
        ) : (
          <>
            {/* Circular progress widget */}
            <CircularWidget $pct={pct} onClick={() => dispatch(setPanelOpen(!panelOpen))} aria-label="Onboarding guide">
              <div className="inner">{pct}%</div>
            </CircularWidget>

            {/* Floating guide panel */}
            {panelOpen && (
              <GuidePanel>
                <PanelHeader>
                  <span className="title">Onboarding Guide</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ position: 'relative' }} ref={menuRef}>
                      <button
                        onClick={() => setMenuOpen((v) => !v)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                          lineHeight: 0,
                          borderRadius: 4,
                        }}
                        aria-label="More options"
                      >
                        <Icon source={MenuHorizontalIcon} />
                      </button>
                      {menuOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: '100%',
                            marginTop: 4,
                            background: '#fff',
                            border: '1px solid #e4e5e7',
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                            zIndex: 10,
                            minWidth: 120,
                          }}
                        >
                          <button
                            onClick={() => {
                              dismissGuide();
                              setMenuOpen(false);
                            }}
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '8px 14px',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontSize: 13,
                              color: 'var(--p-color-text)',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#f6f6f7')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                    <button className="collapse-btn" onClick={() => dispatch(setPanelOpen(false))} aria-label="Collapse guide">
                      <Icon source={ChevronDownIcon} />
                    </button>
                  </div>
                </PanelHeader>

                <GuideContent
                  guide={guide}
                  tasks={TASKS}
                  completedCount={completedCount}
                  pct={pct}
                  activeKey={activeKey}
                  pendingTaskKey={pendingTaskKey}
                  setExpandedKey={(key: string | null) => setExpandedKey(key)}
                  handleNavigate={handleNavigate}
                  onClaimGift={handleClaimGift}
                  showTrialOffer={showTrialOffer}
                />
              </GuidePanel>
            )}
          </>
        ))}

      {/* Ask-review modal */}
      {showReview && (
        <ReviewOverlay>
          <ReviewCard>
            {transitioning ? (
              <DiscountTransition>
                <div className="spinner" />
                <div className="transition-title">Claiming your discount...</div>
                <div className="transition-body">Redirecting to Subscription Plans with your welcome gift ready.</div>
                <div className="discount-badge">WELCOME20 &mdash; 20% off</div>
              </DiscountTransition>
            ) : (
              <>
                <button className="close-btn" onClick={handleDismissReview} aria-label="Close">
                  <Icon source={XIcon} />
                </button>
                <div className="review-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width={80} height={80} viewBox="0 0 80 80" fill="none">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M47.8813 37.0343C48.1933 34.3557 49.9055 31.9244 52.5711 30.8234C55.2366 29.7223 58.162 30.2285 60.2754 31.9054C59.9653 34.5772 58.2531 37.0086 55.5875 38.1096C52.9268 39.2191 49.9966 38.7045 47.8813 37.0343ZM47.8813 37.0343C47.5695 34.3557 45.8572 31.9244 43.1918 30.8234C40.5262 29.7223 37.6007 30.2285 35.4873 31.9054C35.7974 34.5772 37.5097 37.0086 40.1751 38.1096C42.8359 39.2191 45.7662 38.7045 47.8813 37.0343Z"
                      fill="#C6EA94"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M47.8818 38.1326C47.7376 38.1325 47.5949 38.1041 47.4617 38.0488L41.2647 35.483C40.9957 35.3715 40.782 35.1578 40.6706 34.8888C40.5591 34.6198 40.559 34.3175 40.6703 34.0485C40.7818 33.7795 40.9956 33.5658 41.2645 33.4543C41.5335 33.3428 41.8358 33.3427 42.1048 33.454L47.8818 35.8458L53.6587 33.454C53.9278 33.3427 54.2301 33.3428 54.4991 33.4543C54.7681 33.5658 54.9819 33.7795 55.0934 34.0485C55.2046 34.3176 55.2045 34.6198 55.0931 34.8888C54.9816 35.1578 54.7679 35.3715 54.4989 35.483L48.3019 38.0488C48.1687 38.1041 48.026 38.1326 47.8818 38.1326Z"
                      fill="#AFE269"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M46.7832 20.453C46.7832 19.8471 47.2753 19.355 47.8812 19.355C48.4874 19.355 48.9793 19.8471 48.9793 20.453V60.9972C48.9793 61.6031 48.4874 62.0952 47.8812 62.0952C47.2753 62.0952 46.7832 61.6031 46.7832 60.9972V20.453Z"
                      fill="#AFE269"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M46.7828 48.2524C54.7475 48.2524 61.2138 52.9759 61.2138 58.7939C61.2138 64.6118 54.8229 65.3353 46.8581 65.3353C38.8934 65.3353 32.3516 64.6118 32.3516 58.7939C32.3516 52.9759 38.8179 48.2524 46.7828 48.2524Z"
                      fill="#DCB190"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M70.5444 9.71029C71.132 9.56205 71.7295 9.91892 71.8777 10.5065C72.026 11.0941 71.6691 11.6915 71.0815 11.8397L64.7496 13.4369C64.162 13.5851 63.5646 13.2284 63.4164 12.6408C63.2682 12.0532 63.6249 11.4557 64.2125 11.3075L70.5444 9.71029ZM24.6809 11.8397C24.3986 11.7684 24.1562 11.5879 24.0069 11.3379C23.8576 11.0879 23.8137 10.7889 23.8848 10.5065C23.9561 10.2242 24.1366 9.98177 24.3866 9.83247C24.6366 9.68316 24.9356 9.63922 25.218 9.71029L31.5499 11.3075C31.8322 11.3789 32.0746 11.5594 32.2239 11.8094C32.3732 12.0594 32.4172 12.3584 32.3461 12.6408C32.2747 12.9231 32.0942 13.1655 31.8442 13.3148C31.5942 13.464 31.2952 13.508 31.0128 13.4369L24.6809 11.8397ZM69.5979 20.0311C70.1927 20.1467 70.5817 20.7236 70.4661 21.3184C70.3505 21.9133 69.7736 22.3024 69.1788 22.1867L64.1573 21.2105C63.8715 21.1548 63.6195 20.9879 63.4567 20.7465C63.2939 20.5051 63.2336 20.209 63.289 19.9232C63.3447 19.6374 63.5116 19.3854 63.753 19.2226C63.9944 19.0598 64.2906 18.9995 64.5764 19.0549L69.5979 20.0311ZM26.5836 22.1867C25.9888 22.3024 25.4119 21.9133 25.2963 21.3184C25.1806 20.7236 25.5697 20.1467 26.1646 20.0311L31.186 19.0549C31.4718 18.9994 31.768 19.0597 32.0094 19.2225C32.2508 19.3853 32.4177 19.6373 32.4733 19.9232C32.5288 20.209 32.4685 20.5052 32.3057 20.7466C32.1429 20.988 31.8909 21.1549 31.6051 21.2105L26.5836 22.1867ZM63.9617 1.5037C64.4305 1.1197 65.1229 1.18872 65.5069 1.65758C65.8909 2.12645 65.8219 2.81884 65.353 3.20284L61.3954 6.44394C60.9265 6.82778 60.2341 6.75892 59.8501 6.29005C59.4661 5.82119 59.5352 5.1288 60.004 4.7448L63.9617 1.5037ZM30.4093 3.20284C30.1842 3.01824 30.0415 2.75178 30.0126 2.46203C29.9838 2.17227 30.0712 1.88293 30.2556 1.65758C30.4402 1.43238 30.7066 1.28966 30.9963 1.26081C31.286 1.23195 31.5754 1.31932 31.8007 1.5037L35.7584 4.7448C36.2272 5.1288 36.2963 5.82119 35.9123 6.29005C35.5283 6.75892 34.8359 6.82778 34.367 6.44394L30.4093 3.20284Z"
                      fill="#F76C82"
                    />
                    <path
                      d="M48.3895 11.9879C49.595 10.1314 50.93 9.20264 53.3054 9.20264C56.2994 9.20264 58.7286 11.3716 58.7286 14.043C58.7286 18.7628 53.5705 23.4826 48.2844 28.2012C48.1738 28.3008 48.0302 28.3559 47.8814 28.3559C47.7326 28.3559 47.589 28.3008 47.4784 28.2012C42.1923 23.4826 37.0342 18.7628 37.0342 14.043C37.0342 11.3716 39.4634 9.20264 42.4576 9.20264C44.8328 9.20264 46.1678 10.1314 47.3747 11.9879C47.4888 12.1647 47.6707 12.2633 47.882 12.2633C48.0922 12.2633 48.2742 12.1647 48.3895 11.9879Z"
                      fill="#F76C82"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M24.4675 54.9108C20.5805 56.8347 16.6529 58.68 12.7422 60.5494L18.2645 73.9042L26.3874 69.9128C27.5588 69.3352 28.7609 69.2328 30.0136 69.5985L38.6307 72.117C41.3631 72.9133 44.7422 71.4472 47.2849 70.4562L63.4457 64.158C67.7017 62.4973 68.7276 53.8753 58.6751 56.8552L47.1427 60.2725C46.8074 59.0457 45.8561 58.0206 44.5558 57.6719L36.7447 55.5635C31.4187 51.76 28.71 52.8126 24.4675 54.9108Z"
                      fill="#EFC7A4"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M47.1526 62.1997L29.6848 57.4907C29.1634 59.4556 30.3315 61.4957 32.2818 62.0187L42.6628 64.8209C44.6131 65.3437 46.6311 64.1648 47.1526 62.1997Z"
                      fill="#D5A889"
                    />
                    <path
                      d="M22.791 75.7772L18.6467 77.5132C17.7494 77.889 16.7133 77.4585 16.3375 76.5529C14.0013 70.887 11.6617 65.2211 9.32543 59.5551C8.94613 58.6393 9.3695 57.6004 10.2803 57.2211L14.4211 55.4851C15.3184 55.1091 16.3579 55.5397 16.7303 56.4453C19.0701 62.1113 21.4062 67.7772 23.7425 73.4431C24.1217 74.359 23.6984 75.3979 22.791 75.7772Z"
                      fill="#E4E8EB"
                    />
                  </svg>
                </div>
                <div className="review-title">How is your experience so far?</div>
                <div className="review-body">
                  We're grateful to support your store. Every review and suggestion reminds our team why we do this.
                </div>
                <div className="stars">
                  {REVIEW_STARS.map((star) => (
                    <button
                      key={star}
                      className="star-btn"
                      onMouseEnter={() => setHoveredStar(star)}
                      onMouseLeave={() => setHoveredStar(null)}
                      onClick={() => handleStarClick(star)}
                      aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                    >
                      {(() => {
                        const active = star <= (hoveredStar ?? 5);
                        return (
                          <svg width="32" height="32" viewBox="0 0 24 24">
                            <path
                              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                              fill={active ? '#FFC200' : 'none'}
                              stroke="#FFC200"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                          </svg>
                        );
                      })()}
                    </button>
                  ))}
                </div>
              </>
            )}
          </ReviewCard>
        </ReviewOverlay>
      )}
    </>
  );
};

export default OnboardingGuide;
